import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { mdiHandshake, mdiPiggyBank, mdiPiggyBankOutline } from '@mdi/js';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import { doGetAllCustomerSummaries } from 'common/state/customers/thunkActions';
import {
  CustomerSummariesState,
  defaultCustomerSummariesState,
} from 'common/state/customers/states';
import { customerSummariesRecipe } from 'common/state/customers/recipes';
import {
  CustomerSummary,
  CustomerSummariesActions,
  CustomerTypes,
} from 'common/state/customers/actions';
import { BaseTable, ColumnProp, Loading } from 'common/components/table/base';
import FadeIn from 'react-fade-in';
import {
  FilterProp,
  FiltersContainer,
  FilterTypes,
  useFiltration,
} from 'common/components/table/filtering';
import { Currency } from 'common/classes/currency';
import { TableTitle } from 'common/components/table/title';
import { CurrencyCodes } from 'common/configs/currency';
import { Link } from 'react-router-dom';
import {
  ProviderSummariesActions,
  ProviderSummary,
} from 'common/state/provider/actions';
import { providerSummariesRecipe } from 'common/state/provider/recipes';
import {
  ProviderSummariesState,
  defaultProviderSummariesState,
} from 'common/state/provider/states';
import { doGetProviderSummaries } from 'common/state/provider/thunkActions';
import { getDayOfWeek, Days } from 'common/utils/time';
import { TextField } from '@mollybet/ui';

const Container = styled.div`
  display: flex;
  flex: 1;
  margin: 1rem;
  justify-content: center;

  .highlight {
    background-color: rgba(128, 128, 0, 0.3);
  }
`;

export const UsernameLink = styled(Link)`
  width: fit-content;
  padding-bottom: 0.125rem;
  border-bottom: 1px dashed;
  cursor: pointer;
  color: inherit;
  text-decoration: none;
`;

const AgentHome = () => {
  const [customerState, customerDispatch] = useProducerWithThunks<
    CustomerSummariesState,
    CustomerSummariesActions
  >(customerSummariesRecipe, defaultCustomerSummariesState);
  const [providerState, providerDispatch] = useProducerWithThunks<
    ProviderSummariesState,
    ProviderSummariesActions
  >(providerSummariesRecipe, defaultProviderSummariesState);
  const [providerSummaryEndDate, setProviderSummaryEndDate] = useState(
    getDayOfWeek(new Date(), Days.SUNDAY).toISOString().slice(0, 10)
  );

  const { summaries: customerSummaries } = customerState;
  const { summaries: providerSummaries } = providerState;

  useEffect(() => {
    customerDispatch(doGetAllCustomerSummaries());
  }, [customerDispatch]);
  useEffect(() => {
    providerDispatch(
      doGetProviderSummaries({ end_date: providerSummaryEndDate })
    );
  }, [providerDispatch, providerSummaryEndDate]);

  const { customers, nsps, totals } = useMemo(() => {
    const customers: CustomerSummary[] = [];
    const nsps: CustomerSummary[] = [];
    const totals: Partial<{ [code in CurrencyCodes]: CustomerSummary }> = {};

    customerSummaries?.forEach((customer) => {
      if (customer.customer_type === CustomerTypes.NSP) {
        nsps.push(customer);
      } else {
        customers.push(customer);
      }

      totals[customer.wallet_currency] = totals[customer.wallet_currency] || {
        wallet_currency: customer.wallet_currency,
        balances: {
          deposits: 0,
          cleared: 0,
          reconciled: 0,
          final_balance: 0,
          outstanding: 0,
          pending: 0,
          adjustments: 0,
        },
      };

      totals[customer.wallet_currency].balances.deposits += parseFloat(
        customer.balances.deposits || '0'
      );
      totals[customer.wallet_currency].balances.cleared += parseFloat(
        customer.balances.cleared || '0'
      );
      totals[customer.wallet_currency].balances.reconciled += parseFloat(
        customer.balances.reconciled || '0'
      );
      totals[customer.wallet_currency].balances.final_balance += parseFloat(
        customer.balances.final_balance || '0'
      );
      totals[customer.wallet_currency].balances.outstanding += parseFloat(
        customer.balances.outstanding || '0'
      );
      totals[customer.wallet_currency].balances.pending += parseFloat(
        customer.balances.pending || '0'
      );
      totals[customer.wallet_currency].balances.adjustments += parseFloat(
        customer.balances.adjustments || '0'
      );
    });

    return { customers, nsps, totals: Object.values(totals) };
  }, [customerSummaries]);

  const customerColumns = useMemo(
    (): ColumnProp<CustomerSummary>[] => [
      {
        key: 'customer_username',
        title: 'Customer',
        valueFormatter: (row: CustomerSummary) => (
          <UsernameLink to={`/customers/${row.customer_username}`}>
            {row.customer_username}
          </UsernameLink>
        ),
        sortValueGenerator: (row: CustomerSummary) =>
          row.customer_username.toLowerCase(),
        width: 112,
      },
      { key: 'customer_country', title: 'Country', width: 132 },
      { key: 'wallet_currency', title: 'Currency', width: 96 },
      {
        key: 'upline_settled',
        title: 'PL Settled',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.balances.cleared, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.cleared, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.cleared),
        width: 128,
      },
      {
        key: 'reconcilled',
        title: 'Reconcilled Under Settlement Threshold',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            row.balances.reconciled,
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.reconciled, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.reconciled),
        width: 184,
      },
      {
        key: 'pending',
        title: 'Pending To Reconcile Prior Weeks',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.balances.pending, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.pending, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.pending),
        width: 184,
      },
      {
        key: 'outstanding',
        title: 'Current Week Outstanding (Cached)',
        valueFormatter: (row: CustomerSummary) => (
          row.balances.outstanding !== null
            ? <div title={`Cached at ${row.balances.outstanding_age}`}>
                {new Currency(
                  row.balances.outstanding,
                  row.wallet_currency
                ).toElement()}
              </div>
            : <Loading small>Loading...</Loading>
        ),
        csvFormatter: (row) =>
          new Currency(row.balances.outstanding, row.wallet_currency).toString({
            symbol: false,
          }),
        width: 128,
      },
      {
        key: 'total',
        title: 'Total PL',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding),
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding),
            row.wallet_currency
          ).toString({ symbol: false }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.cleared) +
          parseFloat(row.balances.reconciled) +
          parseFloat(row.balances.pending) +
          parseFloat(row.balances.outstanding),
        width: 128,
        className: 'highlight',
      },
      {
        key: 'deposits',
        title: 'Deposits / Withdrawals',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.balances.deposits, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.deposits, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.deposits),
        width: 128,
      },
      {
        key: 'ajustments',
        title: 'Adjustments',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            row.balances.adjustments,
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.adjustments, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.adjustments),
        width: 128,
      },
      {
        key: 'final_balance',
        title: 'Client Account Balance',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding) +
              parseFloat(row.balances.deposits) +
              parseFloat(row.balances.adjustments),
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding) +
              parseFloat(row.balances.deposits) +
              parseFloat(row.balances.adjustments),
            row.wallet_currency
          ).toString({ symbol: false }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.cleared) +
          parseFloat(row.balances.reconciled) +
          parseFloat(row.balances.pending) +
          parseFloat(row.balances.outstanding) +
          parseFloat(row.balances.deposits) +
          parseFloat(row.balances.adjustments),
        width: 160,
        className: 'highlight',
      },
      {
        key: 'wallet_min_balance',
        title: 'Min. Balance',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.wallet_min_balance, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.wallet_min_balance, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.wallet_min_balance),
        width: 128,
      },
      {
        key: 'wallet_credit',
        title: 'Credit Limit',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.wallet_credit, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.wallet_credit, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.wallet_credit),
        width: 128,
      },
      {
        key: 'wallet_threshold',
        title: 'Profit / Losses - Settlement Threshold',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.wallet_threshold, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.wallet_threshold, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.wallet_threshold),
        width: 184,
      },
      {
        key: 'last_placement_date',
        title: 'Last Placement Date',
        valueFormatter: (row: CustomerSummary) =>
          new Date(row.last_placement_date).toLocaleDateString(),
        width: 160,
      },
    ],
    []
  );

  const filters = useMemo(
    (): FilterProp<CustomerSummary>[] => [
      {
        key: 'customer_username',
        label: 'Username',
        type: FilterTypes.FREE_TEXT,
      },
    ],
    []
  );

  const [customerRows, customerFilterComponents, customerFilterHooks] =
    useFiltration(customers || [], filters);

  const [nspRows, nspFilterComponents, nspFilterHooks] = useFiltration(
    nsps || [],
    filters
  );

  const providerColumns = useMemo(
    (): ColumnProp<ProviderSummary>[] => [
      {
        key: 'username',
        title: 'Username',
        valueFormatter: (row: ProviderSummary) => (
          <UsernameLink to={`/providers/${row.username}`}>
            {row.username}
          </UsernameLink>
        ),
        sortValueGenerator: (row: ProviderSummary) =>
          row.username ? row.username.toLowerCase() : row.username,
      },
      { key: 'ccy_code', title: 'Currency' },
      {
        key: 'totus_bookie_deposits',
        title: 'Totus Deposits',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.totus_bookie_deposits, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.totus_bookie_deposits, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'customer_bookie_deposits',
        title: 'Customer Deposits',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.customer_bookie_deposits, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.customer_bookie_deposits, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'pl',
        title: 'Profit/Loss',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.pl, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.pl, row.ccy_code).toString({ symbol: false }),
      },
      {
        key: 'pl_settled',
        title: 'Profit/Loss Settled',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.pl_settled, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.pl_settled, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'comms',
        title: 'Commissions',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.comms, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.comms, row.ccy_code).toString({ symbol: false }),
      },
      {
        key: 'singbet_dividend',
        title: 'Araco - SingBet `dividend`',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.singbet_dividend, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.singbet_dividend, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'commission_settled',
        title: 'Commission Settled',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.commission_settled, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.commission_settled, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'provider_adjustment',
        title: 'Adjustments',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.provider_adjustment, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.provider_adjustment, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'net_balance',
        title: 'Net Balance',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.net_balance, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.net_balance, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'net_balance_gbp',
        title: 'Net Balance (GBP)',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.net_balance_gbp, CurrencyCodes.GBP).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.net_balance_gbp, CurrencyCodes.GBP).toString({
            symbol: false,
          }),
      },
      {
        key: 'pl_settlement_threshold',
        title: 'Profit/Loss Settlement Threshold',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(row.pl_settlement_threshold, row.ccy_code).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.pl_settlement_threshold, row.ccy_code).toString({
            symbol: false,
          }),
      },
      {
        key: 'comms_settlement_threshold',
        title: 'Commissions Settlement Threshold',
        valueFormatter: (row: ProviderSummary) =>
          new Currency(
            row.comms_settlement_threshold,
            row.ccy_code
          ).toElement(),
        csvFormatter: (row: ProviderSummary) =>
          new Currency(row.comms_settlement_threshold, row.ccy_code).toString({
            symbol: false,
          }),
      },
    ],
    []
  );

  const totalsColumns = useMemo(
    (): ColumnProp<CustomerSummary>[] => [
      { key: 'spacer_1', title: ' ', valueFormatter: () => '', width: 112 },
      { key: 'spacer_2', title: ' ', valueFormatter: () => '', width: 132 },
      { key: 'wallet_currency', title: 'Currency', width: 96 },
      {
        key: 'upline_settled',
        title: 'PL Settled',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.balances.cleared, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.cleared, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.cleared),
        width: 128,
      },
      {
        key: 'reconcilled',
        title: 'Reconcilled Under Settlement Threshold',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            row.balances.reconciled,
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.reconciled, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.reconciled),
        width: 184,
      },
      {
        key: 'pending',
        title: 'Pending To Reconcile Prior Weeks',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.balances.pending, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.pending, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.pending),
        width: 184,
      },
      {
        key: 'outstanding',
        title: 'Current Week Outstanding (Cached)',
        valueFormatter: (row: CustomerSummary) => (
          row.balances.outstanding !== null
            ? <div title={`Cached at ${row.balances.outstanding_age}`}>
                {new Currency(
                  row.balances.outstanding,
                  row.wallet_currency
                ).toElement()}
              </div>
            : <Loading small>Loading...</Loading>
        ),
        csvFormatter: (row) =>
          new Currency(row.balances.outstanding, row.wallet_currency).toString({
            symbol: false,
          }),
        width: 128,
      },
      {
        key: 'total',
        title: 'Total PL',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding),
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding),
            row.wallet_currency
          ).toString({ symbol: false }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.cleared) +
          parseFloat(row.balances.reconciled) +
          parseFloat(row.balances.pending) +
          parseFloat(row.balances.outstanding),
        width: 128,
        className: 'highlight',
      },
      {
        key: 'deposits',
        title: 'Deposits / Withdrawals',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.balances.deposits, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.deposits, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.deposits),
        width: 128,
      },
      {
        key: 'ajustments',
        title: 'Adjustments',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            row.balances.adjustments,
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.adjustments, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.adjustments),
        width: 128,
      },
      {
        key: 'final_balance',
        title: 'Client Account Balance',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding) +
              parseFloat(row.balances.deposits) +
              parseFloat(row.balances.adjustments),
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding) +
              parseFloat(row.balances.deposits) +
              parseFloat(row.balances.adjustments),
            row.wallet_currency
          ).toString({ symbol: false }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.cleared) +
          parseFloat(row.balances.reconciled) +
          parseFloat(row.balances.pending) +
          parseFloat(row.balances.outstanding) +
          parseFloat(row.balances.deposits) +
          parseFloat(row.balances.adjustments),
        width: 160,
        className: 'highlight',
      },
    ],
    []
  );

  return (
    <Container>
      <FadeIn>
        <TableTitle icon={mdiPiggyBank} title="Customer Summary" />
        <FiltersContainer hooks={customerFilterHooks}>
          {customerFilterComponents.customer_username}
        </FiltersContainer>
        <br />
        <BaseTable
          columns={customerColumns}
          rows={customerRows}
          csvName="Customers"
          defaultSortColumn="customer_username"
          isLoading={customerState.pending}
          error={customerState.error}
        />
        <TableTitle icon={mdiPiggyBankOutline} title="NSP Summary" />
        <FiltersContainer hooks={nspFilterHooks}>
          {nspFilterComponents.customer_username}
        </FiltersContainer>
        <br />
        <BaseTable
          columns={customerColumns}
          rows={nspRows}
          csvName="NSPs"
          defaultSortColumn="customer_username"
          isLoading={customerState.pending}
          error={customerState.error}
        />
        <TableTitle icon={mdiPiggyBankOutline} title="Totals" />
        <BaseTable
          columns={totalsColumns}
          rows={totals}
          csvName="Totals"
          isLoading={customerState.pending}
          error={customerState.error}
          paginate={false}
        />
        <TableTitle icon={mdiHandshake} title="Providers" />
        <BaseTable
          columns={providerColumns}
          rows={providerSummaries}
          csvName="Providers"
          defaultSortColumn="username"
          isLoading={providerState.pending}
          error={providerState.error}
          extraButtons={
            <TextField
              type="date"
              value={providerSummaryEndDate}
              label="To"
              onChange={(event) =>
                setProviderSummaryEndDate(event.currentTarget.value)
              }
            />
          }
        />
      </FadeIn>
    </Container>
  );
};

export default AgentHome;
